import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from "@angular/core";
import { percentScale } from "@tylertech/common-components-module";
import { NavigationService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, Tick, TooltipItem } from "chart.js";
import { OpenEnrollmentCompleteData } from "../../../interfaces/openEnrollmentCompleteData";
import { OpenEnrollmentChartService } from "../../../services";
import { ChartService } from "@tylertech/charting-module";

@Component({
    selector: "open-enrollment-complete-chart",
    templateUrl: "./openEnrollmentCompleteChart.tmpl.html",
    standalone: false
})
export class OpenEnrollmentCompleteChartComponent implements OnChanges
{
    private $navigationService = inject(NavigationService);
    private readonly $openEnrollmentChartService = inject(OpenEnrollmentChartService);
    private readonly $chartService = inject(ChartService);

    chartConfig: ChartConfiguration<"bar">;

    @Input() private data: OpenEnrollmentCompleteData;
    @Input() index: number;
    @Output() readonly drillIn = new EventEmitter();

    get benefitPlanUrl(): string
    {
        return this.hasPlan
            ? this.$navigationService.getLinkToApp("personnelII/employee/directory", { BenefitPlan: this.planCode }) as string
            : undefined;
    }

    get hasData(): boolean
    {
        return !!this.data;
    }

    get chartId(): string
    {
        return "openEnrollmentChart-" + this.index;
    }

    get hasPlan(): boolean
    {
        return this.hasData ? !Utilities.IsNullOrUndefinedOrEmpty(this.data.PlanCode) : false;
    }

    get plan(): string
    {
        return this.hasData ? this.data.PlanCode : "";
    }

    get date(): Date
    {
        return this.hasData ? this.data.EffectiveDate : null;
    }

    get complete(): number
    {
        return this.hasData ? this.data.Complete : null;
    }

    get incomplete(): number
    {
        return this.hasData ? this.data.Incomplete : null;
    }

    get total(): number
    {
        return this.hasData ? this.data.Total : null;
    }

    get planCode(): string
    {
        if (this.hasData && !Utilities.IsNullOrUndefinedOrEmpty(this.plan))
        {
            return this.plan.split(" - ")[0];
        }
        return "";
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (!Utilities.IsNullOrUndefined(changes.data.currentValue))
        {
            this.displayChart();
        }
    }

    private displayChart(): void
    {
        const chartColors = this.$chartService.getColors(5);
        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: "y",
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (item: TooltipItem<"bar">[]): string | string[] =>
                            {
                                const first = item[0];
                                const chart = first.chart;
                                const label = first.datasetIndex === 0 ? "Complete" : "Incomplete";
                                const value = chart.data.datasets[first.datasetIndex].data[first.dataIndex]?.toString() || "";
                                return `${label}: ${percentScale(value)}`;
                            },
                            label: (_tooltipItem: TooltipItem<"bar">,): string | string[] => "",
                        },
                        mode: 'nearest',
                        intersect: true,
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        beginAtZero: true,
                        min: 0,
                        max: 100,
                        ticks: {
                            maxTicksLimit: 5,
                            stepSize: 25,
                            callback: (value: number | string, _index: number, _ticks: Tick[]): string | number | null | undefined =>
                            {
                                return percentScale(value);
                            },
                        },
                    },
                    y: { stacked: true },
                },
                onClick: (_event: ChartEvent, _elements: ActiveElement[], _chart: Chart): void =>
                {
                    this.$openEnrollmentChartService.SetPlanCode(this.planCode);
                    this.drillIn.emit();
                },
            },
            data: {
                labels: ["Complete"],
                datasets: [
                    {
                        data: [this.data.PercentComplete],
                        backgroundColor: chartColors[1],
                    },
                    {
                        data: [this.data.PercentIncomplete],
                        backgroundColor: chartColors[4],
                    }
                ],
            },
        };
    }
}
